import React, { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { magicLogin } from 'slices/authSlice'
import routes from 'constants/routes'
import { Loader2 } from 'lucide-react'

/**
 * Passwordless entry point. Reads the token from the URL, exchanges it for a
 * session, and lands the trial user in the app. Invalid/expired links show a
 * friendly message with a path to the normal login.
 */
const MagicLoginScreen = () => {
	const { token } = useParams()
	const dispatch = useDispatch<any>()
	const navigate = useNavigate()
	const [error, setError] = useState<string | null>(null)
	const attempted = useRef(false)

	useEffect(() => {
		if (attempted.current) return
		attempted.current = true

		if (!token) {
			setError('This link is invalid or has expired.')
			return
		}

		dispatch(magicLogin({ token }))
			.unwrap()
			.then(() => navigate(routes.HOME, { replace: true }))
			.catch((err: any) => {
				setError(typeof err === 'string' ? err : 'This link is invalid or has expired.')
			})
	}, [dispatch, navigate, token])

	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
			<div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
				{error ? (
					<>
						<h1 className="mb-2 text-xl font-bold text-gray-900">Link no longer valid</h1>
						<p className="mb-6 text-sm text-gray-600">{error}</p>
						<button
							type="button"
							onClick={() => navigate(routes.LOGIN, { replace: true })}
							className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90"
						>
							Go to sign in
						</button>
					</>
				) : (
					<>
						<Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" />
						<h1 className="mb-1 text-lg font-semibold text-gray-900">Opening your workspace…</h1>
						<p className="text-sm text-gray-500">Just a moment while we sign you in.</p>
					</>
				)}
			</div>
		</div>
	)
}

export default MagicLoginScreen
